export interface ConversationReadState {
  lastReadAt: string | null;
  /** Legacy opaque cursor. Never parse it as a timestamp or compare its numeric value. */
  lastReadCursor: string | null;
  hasUnreadMessages: boolean | null;
  hasUnreadNotifications: boolean | null;
  versions: { reader: string | null; messageHead: string | null; notifications: string | null };
  readBoundaryFormat?: 'timestamp-id-v1' | null;
  lastReadMessageCreatedAt?: string | null;
  lastReadMessageId?: string | null;
  latestMessageCreatedAt?: string | null;
  latestMessageId?: string | null;
}

const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
const MESSAGE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const text = (value: unknown): string | null => typeof value === 'string' && value.length > 0 ? value : null;

/** Timestamp fields describe server state only. ACK always sends the opaque token, preserving the
 * server's full timestamp precision and captured boundary even when the JS Date codec rounds it. */
export function parseConversationReadState(value: unknown): ConversationReadState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid conversation read state');
  const raw = value as Record<string, unknown>;
  const format = raw.readBoundaryFormat;
  if (format != null && format !== '' && format !== 'timestamp-id-v1') throw new Error('Unsupported conversation read boundary');
  const versions = raw.versions as Record<string, unknown> | undefined;
  const result: ConversationReadState = {
    lastReadAt: text(raw.lastReadAt), lastReadCursor: text(raw.lastReadCursor),
    hasUnreadMessages: typeof raw.hasUnreadMessages === 'boolean' ? raw.hasUnreadMessages : null,
    hasUnreadNotifications: typeof raw.hasUnreadNotifications === 'boolean' ? raw.hasUnreadNotifications : null,
    versions: { reader: text(versions?.reader), messageHead: text(versions?.messageHead), notifications: text(versions?.notifications) },
  };
  if (format === 'timestamp-id-v1') {
    for (const [dateKey, idKey] of [['lastReadMessageCreatedAt', 'lastReadMessageId'], ['latestMessageCreatedAt', 'latestMessageId']] as const) {
      const date = raw[dateKey], id = raw[idKey];
      if (date == null && id == null) continue;
      if (typeof date !== 'string' || !TIMESTAMP.test(date) || !Number.isFinite(Date.parse(date))
        || typeof id !== 'string' || !MESSAGE_ID.test(id)) throw new Error('Invalid timestamp message boundary');
    }
    Object.assign(result, { readBoundaryFormat: format, lastReadCursor: null,
      lastReadMessageCreatedAt: text(raw.lastReadMessageCreatedAt), lastReadMessageId: text(raw.lastReadMessageId),
      latestMessageCreatedAt: text(raw.latestMessageCreatedAt), latestMessageId: text(raw.latestMessageId) });
  }
  return result;
}
